import type { ChartData } from 'chart.js';

export interface TrendSeries {
  labels: string[];
  data: number[];
}

export interface DashboardStats {
  activeAssistants: number;
  totalConversations: number;
  messagesToday: number;
  successRate: number;
}

// Normalize stats with safe defaults
export function transformDashboardStats(input: any): DashboardStats {
  return {
    activeAssistants: numberOrZero(input?.activeAssistants),
    totalConversations: numberOrZero(input?.totalConversations),
    messagesToday: numberOrZero(input?.messagesToday),
    successRate: numberOrZero(input?.successRate),
  };
}

// Normalize simple {labels, data} into Chart.js friendly structure
export function transformSeriesToChart(
  input: any,
  datasetLabel: string,
  options?: { color?: string; fill?: boolean }
): ChartData<'line' | 'bar', number[], string> {
  const series: TrendSeries = {
    labels: arrayOfStrings(input?.labels),
    data: arrayOfNumbers(input?.data),
  };

  return {
    labels: series.labels,
    datasets: [
      {
        label: datasetLabel,
        data: series.data,
        borderColor: options?.color || 'var(--color-chart-line, rgb(147, 51, 234))',
        backgroundColor: withAlpha(options?.color || 'rgb(147, 51, 234)', 0.12),
        fill: options?.fill ?? true,
        tension: 0.4,
      } as any,
    ],
  };
}

export function transformCategoryToChart(
  input: any,
  datasetLabel: string,
  options?: { colors?: string[] }
): ChartData<'bar' | 'doughnut', number[], string> {
  const labels = arrayOfStrings(input?.labels);
  const data = arrayOfNumbers(input?.data);
  const colors =
    options?.colors || [
      'rgba(147, 51, 234, 0.8)',
      'rgba(37, 211, 102, 0.8)',
      'rgba(251, 191, 36, 0.8)',
      'rgba(59, 130, 246, 0.8)',
      'rgba(239, 68, 68, 0.8)',
    ];

  return {
    labels,
    datasets: [
      {
        label: datasetLabel,
        data,
        backgroundColor: labels.map((_, i) => colors[i % colors.length]),
        borderWidth: 0,
      } as any,
    ],
  };
}

// Helpers
function numberOrZero(value: any): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function arrayOfStrings(value: any): string[] {
  if (Array.isArray(value)) return value.map((v) => String(v));
  return [];
}

function arrayOfNumbers(value: any): number[] {
  if (Array.isArray(value))
    return value.map((v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    });
  return [];
}

function withAlpha(rgbOrHex: string, alpha: number): string {
  if (rgbOrHex.startsWith('#')) {
    // crude hex -> rgba conversion for #RRGGBB
    const r = parseInt(rgbOrHex.slice(1, 3), 16);
    const g = parseInt(rgbOrHex.slice(3, 5), 16);
    const b = parseInt(rgbOrHex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  if (rgbOrHex.startsWith('rgb(')) {
    const nums = rgbOrHex
      .slice(4, -1)
      .split(',')
      .map((x) => Number(x.trim()));
    if (nums.length >= 3) return `rgba(${nums[0]}, ${nums[1]}, ${nums[2]}, ${alpha})`;
  }
  return rgbOrHex;
}

// Guarded fetch result mapper
export type GuardedResult<T> = { ok: true; data: T } | { ok: false; error: { message: string; code?: number } };

export async function guardedFetch<T>(p: Promise<{ data: T }>): Promise<GuardedResult<T>> {
  try {
    const res = await p;
    return { ok: true, data: res.data };
  } catch (e: any) {
    return {
      ok: false,
      error: {
        message: e?.response?.data?.message || e?.message || 'Request failed',
        code: e?.response?.status,
      },
    };
  }
}


